import React,{ useState,useEffect,useRef} from "react";
import moment from "moment";
import axios from '../../../plugins/axios';
import Paging from "./Common/Paging.jsx"
import { useTranslation } from 'react-i18next';
import Toast from "../../../plugins/useToast";

import {ReactComponent as IconSearch } from "../../../assets/icons/ic-search.svg";
import {ReactComponent as IconArrowUp} from '../../../assets/icons/ic-arrow-up.svg'
import {ReactComponent as IconArrowDown} from '../../../assets/icons/ic-arrow-down.svg'
import {ReactComponent as IconCheck} from '../../../assets/icons/ic-check_blue.svg';
import {ReactComponent as IconEdit} from '../../../assets/icons/ic-admin-edit.svg';

const Department = ({handleSetConfirm,handleCloseConfirm}) => {
    const { t } = useTranslation()
    const isFirstRender = useRef(true);
    const [searchValue, setSearchValue] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [itemCount, setItemCount] = useState(50); 
    const countOptions = [50, 100, t('admin.all')];    
    const [searchSort, setSearchSort] = useState({
        column: '',
        param : '',
        order: 'ASC'
    });
    const [pagingInfo, setPagingInfo] = useState({
        currentPage: 1,
        totalPages: 50,        
        pageSize: 5,
        totalCount : 0
    });    
    const [deptData, setDeptData] = useState([]);
    const [isAllSelected, setIsAllSelected] = useState(false); //전체 아이템 선택 관리 상태.
    const [originalDeptName, setOriginalDeptName] = useState(null); // 원래 Department 이름을 저장하는 상태

    // ✅ 정렬 시 Icon 변경
    const getSortIcon = (column) => {
        if (searchSort.column === column) {
            return searchSort.order === 'ASC' ? 
                <IconArrowUp className="inline-block" /> : 
                <IconArrowDown className="inline-block" />;
        }
        return <IconArrowDown className="inline-block" />; // 기본 아이콘
    };

    // ✅ Sort UseState 변경
    const handleSort = (column,param) => {
        setSearchSort((prev) => ({
            column: column,
            param : param,
            order: prev.column === column && prev.order === 'ASC' ? 'DESC' : 'ASC'
        }));
    };    

    // ✅ 페이지 이동 함수
    const goToPage = (page) => {
        if (page < 1 || page > pagingInfo.totalPages) return;
        setPagingInfo((prev) => ({ ...prev, currentPage: page })); 
        getDepartmentList(page-1);
    };

    // ✅ Enter키 입력 시 검색 
    const handleEnter = (e) => {
        if (e.keyCode === 13) {            
            getDepartmentList(0);
        }
    }

    // ✅ Edit Input에서 Enter키 입력 시 Department 수정
    const handleEnterForEditDept = (e, id, name) => {
        if (e.keyCode === 13) {            
            handleEditDept(id, name);
        }
    }

    // ✅ DepartmentList 가져오기
    const getDepartmentList = async (page) => {
        try {
            const keyword = searchValue;            
            const size = itemCount == t('admin.all') ? 0 : itemCount;
            const sortBy = searchSort.param; 
            const sort = searchSort.order;
            const url = `/departments?page=${page}&${keyword ? 'keyword=' + keyword + '&' : ''}${size ? 'size=' + size + '&' : ''}${sortBy ? 'sortBy=' + sortBy + '&' : ''}${sort ? 'sort=' + sort : ''}`;

            const resDept = await axios.get(url);
            if(resDept.code != 200) throw resDept;       

            //데이터 시 isCheck를 넣어주고, CheckBox로 쓰기 위함.
            const deptListWithCheck = resDept.data.content.map(dept => ({
                ...dept,
                isCheck: false,
                isEdit : false
            }));

            setDeptData(deptListWithCheck);
            setIsAllSelected(false); // 전체 선택 해제

            //Paging
            setPagingInfo({
                currentPage: (resDept.data.page+1),
                totalPages: resDept.data.totalPages,
                pageSize: resDept.data.size,
                totalCount : resDept.data.totalElements
            });
        } catch (err) {
            console.warn(err)
        }
    }

    // ✅ Department 수정
    const handleEditDept = async (id,name) => {
        try {
            const payload = {
                name : name
            }

            const res = await axios.put(`/department/${id}`, payload);
            if(res.code != 200) throw res;
            
            Toast({ isSuccess: true, msg: t('admin.department.editSuccess') });

            // 수정 성공 시, deptData에서 해당 아이템의 isEdit을 false로 변경하고, 이름을 업데이트
            const updatedData = deptData.map((item) =>
                item.id === id ? { ...item, isEdit: false, name: name } : item
            );

            setDeptData(updatedData);
        }
        catch (err) {
            Toast({ isSuccess: false, msg: t('admin.department.editFail') });
            console.warn(err);  
        }
    }
    
    // ✅ Department 추가
    const handleSaveDept = async (name) => {
        try{
            if(!name) {
                handleSetConfirm('department','add', true, null, null, t('admin.department.confirm.addEmptyName'));
            }
            const payload = {
                name : name
            }
            const res = await axios.post('/department', payload);
            if(res.code != 200) throw res;

            Toast({ isSuccess: true, msg: t('admin.department.confirm.addSuccess') });
            handleCloseConfirm();
            getDepartmentList(0);
        }
        catch(err){
            Toast({ isSuccess: false, msg: t('admin.department.confirm.addFail') });
            handleSetConfirm('department','add', true, handleSaveDept,null,err.message,t('admin.department.confirm.addTitle'),'',25);            
            console.warn(err);
        }
    }

    // ✅ 선택된 부서 삭제
    const handleDeleteDept = async () => {
        try{
            const selectedDepts = deptData.filter(dept => dept.isCheck);
            if (selectedDepts.length === 0) {
                Toast({ isSuccess: false, msg: t('admin.department.noDeptSelectedForDelete') });
                return;
            }            

            const deptsId = selectedDepts.map(dept => dept.id);
            const res = await axios.delete('/departments', {params : {departmentIds : deptsId.join(',')}}); // Rodney - 단건 삭제만 현재는 가능함.            

            if(res.code != 200) throw res;

            Toast({ isSuccess: true, msg: t('admin.department.deleteSuccess') });            
            getDepartmentList(0);
        }
        catch(err){
            Toast({ isSuccess: false, msg: t('admin.department.deleteFail') });
            console.warn(err);
        }
    }

    // ✅ 검색, itemCount, Paging, 정렬 시 List 변경.
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        getDepartmentList(0);
    }, [itemCount,searchSort]);

    useEffect(() => {
        if (isAllSelected) {
            // 전체 선택: 모든 아이템의 isCheck를 true로 변경
            const updatedData = deptData.map(dept => ({
                ...dept,
                isCheck: true
            }));
            setDeptData(updatedData);
        } else {
            // 전체 해제: 모든 아이템의 isCheck를 false로 변경
            const updatedData = deptData.map(dept => ({
                ...dept,
                isCheck: false
            }));
            setDeptData(updatedData);
        }
    }, [isAllSelected]);

    useEffect(() => {    
        getDepartmentList(0);    
        const handleClickOutside = (event) => {
            if (!event.target.closest('.select-dropdown')) {
                setIsDropdownOpen(null);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    const isAnyChecked = deptData.some(dept => dept.isCheck);

    return (        
        <div>
            <h1 className="admin-title mb-[24px]">{t('admin.department.title')}<span className="text-[#E1E4E5] font-pretendard text-[16px] font-normal leading-[160%] pl-[8px]">{`(${itemCount == t('admin.all') ? pagingInfo.totalCount : (pagingInfo.totalCount < (itemCount * pagingInfo.currentPage) ? pagingInfo.totalCount : (itemCount * pagingInfo.currentPage))}/${pagingInfo.totalCount})`}</span></h1>
            <div className="flex justify-between mb-[17px]">
                <div className="flex gap-[8px]">
                    <div className={`${!isAnyChecked ? 'disabled !cursor-default' : ''} admin-normal-btn w-[88px] h-[32px]`} onClick={isAnyChecked ? () => handleSetConfirm('department','delete', true,handleDeleteDept,null,'','',t(`admin.department.confirm.delete`, { department: deptData.filter(dept => dept.isCheck).map(dept => dept.name).join(',')})) : null  }>
                        <span className="btn-content">{t('admin.delete')}</span>
                    </div>
                </div> 
                <div className="flex items-center gap-[12px]"> 
                    <div className="relative">                        
                        <input
                            type="text"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            onKeyDown={handleEnter}
                            placeholder={t('admin.searchPlaceholder')}
                            className="admin-input w-[280px] "
                            maxLength={50}
                        />
                        <span className="ml-2 absolute bottom-[6px] right-2" onClick={() => getDepartmentList(0)}>
                            <IconSearch className="cursor-pointer"/>
                        </span>
                    </div>                    
                    <div className="w-[1px] h-[24px] bg-[#0D0D0F]"></div>                                        
                    <div className="relative select-dropdown">
                        <div className="flex items-center justify-between w-[120px] h-[32px] px-[8px] rounded-[6px] border border-[#0D0D0F] text-[#DEDEDE] cursor-pointer bg-gradient-to-b from-[#5B5C60] to-[#303237] shadow-[inset_0_-2px_1px_0_rgba(255,255,255,0.10),inset_0_2px_0_0_rgba(255,255,255,0.12)]"
                        onClick={(e) => { e.stopPropagation(); setIsDropdownOpen(!isDropdownOpen);}}>
                            <div className="w-[200px]">{itemCount} {t('admin.items')}</div>
                            <div className="pointer-events-none">
                                {isDropdownOpen ? <IconArrowUp /> : <IconArrowDown />}
                            </div>
                        </div>
                        {isDropdownOpen && (
                            <div className="absolute top-[35px] bg-[#1F1F1F] w-[120px] py-[8px] rounded-[6px] border border-[#5E5E5E] shadow-[0px_12px_12px_0px_rgba(0,0,0,0.4)] z-20">
                                {
                                    countOptions.map((option, index) => {
                                        return (
                                            <div key={index} className={`bg-[#1F1F1F] cursor-pointer h-[32px] flex items-center justify-between px-[8px] hover:bg-[#2F2F2F] 
                                                ${itemCount === option ? "text-[#3182F7]" : "text-white"}`}
                                                onClick={(e) => { e.stopPropagation(); setItemCount(option); setIsDropdownOpen(false); }}>
                                                {option}
                                                {itemCount === option && (
                                                    <IconCheck className="w-4 h-4 ml-2" />
                                                )}
                                            </div>
                                        )
                                    })
                                }
                            </div>
                        )}
                    </div>
                    <div className="w-[1px] h-[24px] bg-[#0D0D0F]"></div>                    
                    <div className="admin-normal-btn add w-[120px] h-[32px]" onClick={() => handleSetConfirm('department','add', true, handleSaveDept, null, null,t('admin.department.confirm.addTitle'),'',25)}>
                        <span className="btn-content">{t('admin.add')}</span>
                    </div>
                </div>
            </div>
            <div className="mb-[26px]">
                <div className="scrollbar scrollbar-10px" style={{ maxHeight: '680px', borderBottom: '2px solid #0D0D0F' }}>
                    <table className="admin-table w-full">
                        <thead>
                            <tr className="h-[42px]">
                                <th className="w-[80px]">
                                    <div className="w-full flex justify-center items-center">
                                        <div className="rounded-[6px] p-[6px] w-[36px]">
                                            <label className="flex gap-[4px]">
                                                <input
                                                    type="checkbox"
                                                    value={isAllSelected}
                                                    checked={isAllSelected}
                                                    onChange={() => setIsAllSelected(!isAllSelected)}
                                                />
                                            </label>
                                        </div>
                                    </div>
                                </th>                            
                                <th className="w-[490px]" onClick={() => handleSort('Department Name','NAME')}>{t('admin.department.departmentName')} {getSortIcon('Department Name')}</th>
                                <th className="w-[160px]" onClick={() => handleSort('Members','USER_COUNT')}>{t('admin.department.members')} {getSortIcon('Members')}</th>
                                <th className="w-[320px]" onClick={() => handleSort('Date Created','CREATED_AT')}>{t('admin.department.dateCreated')} {getSortIcon('Date Created')}</th>
                                <th className="w-[320px]" onClick={() => handleSort('Last Modified Date','UPDATED_AT')}>{t('admin.department.lastModifiedDate')} {getSortIcon('Last Modified Date')}</th>
                                <th className="w-[140px] !cursor-default">{t('admin.edit')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                deptData && deptData.map((dept, index) => {
                                    return (
                                        <tr className={`${dept.isCheck ? 'selected-row' : `${index % 2 === 0 ? 'bg-[#202024]' : 'bg-[#29292E]'}`} h-[42px]`} key={`${dept.id}-${index}`}>
                                            <td>
                                                <div className="w-full flex justify-center items-center">
                                                    <div className="rounded-[6px] p-[6px] w-[36px]">
                                                        <label className="flex gap-[4px]">
                                                            <input
                                                                type="checkbox"
                                                                value={dept.isCheck}
                                                                checked={dept.isCheck}                                                            
                                                                onChange={() => {
                                                                    const updatedData = deptData.map((item) =>
                                                                        item.id === dept.id ? { ...item, isCheck: !item.isCheck } : item
                                                                    );
                                                                    setDeptData(updatedData);
                                                                }}
                                                            />
                                                        </label>
                                                    </div>
                                                </div>        
                                            </td>    
                                            <td>
                                                {
                                                    dept.isEdit ? (
                                                        <div className="py-[4px]"> 
                                                            <input
                                                                type="text"
                                                                value={dept.name}
                                                                onChange={(e) => 
                                                                    setDeptData(deptData.map(item => item.id === dept.id ? { ...item, name: e.target.value } : item))
                                                                }
                                                                onKeyDown={(e) => handleEnterForEditDept(e, dept.id, dept.name)}
                                                                className="admin-input w-full h-[30px] text-center"
                                                                maxLength={25}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <>
                                                            {dept.name || '-'}
                                                        </>
                                                    )
                                                }
                                            </td>
                                            <td>{dept.userCount || '-'}</td>
                                            <td>{dept.createdAt && moment(dept.createdAt).isValid() ? moment(dept.createdAt).format("YYYY-MM-DD HH:mm:ss") : '-'}</td>
                                            <td>{dept.updatedAt && moment(dept.updatedAt).isValid() ? moment(dept.updatedAt).format("YYYY-MM-DD HH:mm:ss") : '-'}</td>
                                            <td className="px-[4px]">
                                                {
                                                    dept.isEdit ? (
                                                        <div className="w-full flex justify-center gap-[4px]">
                                                            <div className="admin-normal-btn add w-[64px] h-[28px] p-[7px_8px]" onClick={() => handleEditDept(dept.id, dept.name)}>
                                                                <span className="btn-content !text-[14px]">{t('admin.save')}</span>
                                                            </div>
                                                            <div className="admin-normal-btn w-[64px] h-[28px] p-[7px_8px]" 
                                                                onClick={() => {
                                                                    const updatedData = deptData.map((item) =>
                                                                        item.id === dept.id ? { ...item, isEdit: !item.isEdit, name : originalDeptName } : item
                                                                    );
                                                                    setDeptData(updatedData);
                                                                }}>
                                                                <span className="btn-content !text-[14px]">{t('admin.cancel')}</span>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <IconEdit className="w-[32px] h-[32px] p-[5px] inline-block cursor-pointer" onClick={() => {
                                                            const updatedData = deptData.map((item) =>
                                                                item.id === dept.id ? { ...item, isEdit: !item.isEdit } : item
                                                            );
                                                            setOriginalDeptName(dept.name);
                                                            setDeptData(updatedData);
                                                        }}/>
                                                    )
                                                }
                                            </td>
                                        </tr>
                                    )
                                })                                
                            }                        
                        </tbody>
                    </table>
                </div>
            </div>

            <Paging currentPage={pagingInfo.currentPage} goToPage={goToPage} totalPages={pagingInfo.totalPages} visibleCount={5} />
        </div>
    )
};

export default Department
